
import { Component, Prop, Vue } from 'nuxt-property-decorator'
import { computeDestinationPoint } from 'geolib'
import { GPSForm, GPSLocation, GPSMode } from './model/GeolocationNode'
import { Position } from './utils/geolocationService/Position'
import { GPSLocationExtension } from './utils/geolocationService/GPSLocationExtension'
import {
  GeolocationService,
  GeolocationServiceStatus,
} from './utils/geolocationService/GeolocationService'
import {
  AislocationService,
  AislocationServiceStatus,
} from './utils/aislocationService/AislocationService'

@Component
export default class WebPlayerMap extends Vue {
  readonly style: string = 'mapbox://styles/mapbox/outdoors-v12'
  private firstLocation?: Position

  @Prop({ default: null }) gpsLocations!: GPSLocationExtension[] | null
  @Prop({ default: null }) geolocationService!: GeolocationService | null
  @Prop({ default: null }) aislocationService!: AislocationService | null
  @Prop({ default: false }) isFullScreen!: boolean

  isLoading = true
  userLocation: Position | null = null

  // Map data
  map: any

  get accessToken(): string {
    const MAPBOX_ACCESS_TOKEN = this.$config.MAPBOX_ACCESS_TOKEN
    return MAPBOX_ACCESS_TOKEN ?? ''
  }

  get locations(): GPSLocationExtension[] {
    return (this.gpsLocations ?? []) as GPSLocationExtension[]
  }

  get center(): number[] {
    return [
      this.userLocation?.longitude ?? this.firstLocation?.longitude ?? 0,
      this.userLocation?.latitude ?? this.firstLocation?.latitude ?? 0,
    ]
  }

  private setMapPosition(position: Position) {
    if (!this.firstLocation) {
      this.firstLocation = position
      this.map?.setZoom(13)
    }

    this.userLocation = position
    this.map?.setCenter([
      this.userLocation.longitude,
      this.userLocation.latitude,
    ])
  }

  created() {
    if (this.geolocationService) {
      this.geolocationService!.registerPositionListener((position) => {
        this.setMapPosition(position)
      })

      if (
        this.geolocationService?.getStatus() ===
        GeolocationServiceStatus.WATCHING
      ) {
        this.isLoading = false
      } else {
        this.geolocationService!.registerStatusListener((currentStatus) => {
          if (currentStatus !== GeolocationServiceStatus.WATCHING) return

          this.isLoading = false
        })
      }
    } else if (this.aislocationService) {
      this.aislocationService!.registerPositionListener((position) => {
        this.setMapPosition(position)
      })

      if (
        this.aislocationService?.getStatus() ===
        AislocationServiceStatus.SOCKET_OPEN
      ) {
        this.isLoading = false
      } else {
        this.aislocationService!.registerStatusListener((currentStatus) => {
          switch (currentStatus) {
            case AislocationServiceStatus.SOCKET_OPEN:
              this.isLoading = false
              break
            default:
              this.isLoading = true
              break
          }
        })
      }
    }
  }

  beforeUnmount() {
    this.geolocationService?.destroy()
  }

  onPreviewMapLoaded(payload: any) {
    this.map = payload.map

    this.initCamera()

    this.locations.forEach((location, index) => {
      if (location.showLocation) {
        const color = WebPlayerMap.getRandomColor()
        if (location.mode === GPSMode.Absolute) {
          if (!location.form || location.form === GPSForm.Circle) {
            this.drawAbsoluteCircle(location, color)
          } else if (location.form === GPSForm.Polygon) {
            this.drawAbsolutePolygons(index, location, color)
          }
        } else if (location.mode === GPSMode.Relative) {
          this.drawRelativeCircle(index, location, color)
        }
      }
    })

    this.map.addLayer({
      id: 'add-3d-buildings',
      source: 'composite',
      'source-layer': 'building',
      filter: ['==', 'extrude', 'true'],
      type: 'fill-extrusion',
      minzoom: 13,
      paint: {
        'fill-extrusion-color': '#aaa',
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          13,
          0,
          15.05,
          ['get', 'height'],
        ],
        'fill-extrusion-base': [
          'interpolate',
          ['linear'],
          ['zoom'],
          13,
          0,
          15.05,
          ['get', 'min_height'],
        ],
        'fill-extrusion-opacity': 0.6,
      },
    })
  }

  drawRelativeCircle(index: number, location: GPSLocation, color: string) {
    if (!this.firstLocation) return
    const first = this.firstLocation

    const center = { lng: first.longitude, lat: first.latitude }
    const options = { editable: false, fillColor: color }

    // Draw circle
    const circle = new Vue.prototype.$MapboxCircle(
      center,
      location.distance,
      options
    )
    circle.addTo(this.map)

    if (!location.useDirection) return

    const centerAngle = {
      longitude: first.longitude,
      latitude: first.latitude,
    }
    const minAngle = computeDestinationPoint(
      centerAngle,
      location.distance ?? 0,
      location.minAngle ?? 0
    )
    const maxAngle = computeDestinationPoint(
      centerAngle,
      location.distance ?? 0,
      location.maxAngle ?? 0
    )

    // Draw angle delimiters
    const data = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [
              [centerAngle.longitude, centerAngle.latitude],
              [minAngle.longitude, minAngle.latitude],
            ],
          },
        },
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [
              [centerAngle.longitude, centerAngle.latitude],
              [maxAngle.longitude, maxAngle.latitude],
            ],
          },
        },
      ],
    }

    this.map.addSource('delimiters-' + index, { type: 'geojson', data })

    this.map.addLayer({
      id: 'delimiters-' + index,
      type: 'line',
      source: 'delimiters-' + index,
      layout: {},
      paint: {
        'line-color': 'black',
        'line-width': 2,
      },
    })
  }

  drawAbsoluteCircle(location: GPSLocation, color: string) {
    if (!this.firstLocation) return

    const center = { lng: location.long ?? 0, lat: location.lat ?? 0 }
    const options = { editable: false, fillColor: color }

    // Draw circle
    const circle = new Vue.prototype.$MapboxCircle(
      center,
      location.radius,
      options
    )
    circle.addTo(this.map)
  }

  drawAbsolutePolygons(index: number, location: GPSLocation, color: string) {
    if (!location.polygons || location.polygons.length === 0) return

    const data = {
      type: 'FeatureCollection',
      features: [] as any[],
    }

    // Draw polygons
    location.polygons.forEach((polygon) => {
      data.features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [polygon.coords],
        },
        properties: {},
      })
    })

    this.map.addSource('polygons-' + index, { type: 'geojson', data })

    this.map.addLayer({
      id: 'polygons-' + index,
      type: 'fill',
      source: 'polygons-' + index,
      layout: {},
      paint: {
        'fill-color': color,
        'fill-opacity': 0.5,
      },
    })
  }

  initCamera() {
    if (!this.firstLocation) return

    this.map?.setCenter([
      this.firstLocation.longitude,
      this.firstLocation.latitude,
    ])

    this.map?.setZoom(15)
  }

  static getRandomColor(): string {
    const color = Math.floor(Math.random() * 16777215).toString(16)
    return '#' + color.padEnd(6, '0')
  }
}
